export interface TaskStep {
  number: number;
  title: string;
  detail: string;
}

export interface TaskCriteria {
  id: number;
  text: string;
}

export interface TaskResource {
  title: string;
  url: string;
  type: 'doc' | 'video' | 'lab';
}

export interface TaskDetail {
  itemId: number;
  title: string;
  subtitle: string;
  points: number;
  priority?: 'Low' | 'Medium' | 'High' | 'Critical';
  difficulty: 'Facile' | 'Moyen' | 'Difficile';
  estimatedTime: string;
  objective: string;
  context: string;
  steps: TaskStep[];
  criteria: TaskCriteria[];
  resources: TaskResource[];
}

const TASK_DATA: TaskDetail[] = [
  {
    itemId: 4,
    title: 'Creer les utilisateurs Nina et Luc',
    subtitle: 'US-EN-ID-001 — Provisionnement Entra ID',
    points: 15,
    priority: 'High',
    difficulty: 'Facile',
    estimatedTime: '15 min',
    objective:
      "Creer les comptes utilisateurs nina.martin et luc.durand dans Microsoft Entra ID avec les attributs de metadonnees corrects (jobTitle) pour activer le provisionnement automatique dans les groupes dynamiques.",
    context:
      "Nina a explique que chaque champ est une variable dans une chaine d'automatisation. Le jobTitle 'Cloud Administrator' declenche l'adhesion automatique au groupe IT Cloud Administrators. Le jobTitle 'System Administrator' fait de meme pour IT System Administrators.",
    steps: [
      {
        number: 1,
        title: 'Acceder au portail Azure',
        detail: 'Naviguer vers portal.azure.com > Microsoft Entra ID > Users > New user > Create new user',
      },
      {
        number: 2,
        title: 'Creer le compte de Nina Martin',
        detail:
          "Display Name: Nina Martin | UPN: nina.martin@<tenant>.onmicrosoft.com | Job Title: Cloud Administrator | Department: Engineering | Usage Location: FR",
      },
      {
        number: 3,
        title: 'Creer le compte de Luc Durand',
        detail:
          "Display Name: Luc Durand | UPN: luc.durand@<tenant>.onmicrosoft.com | Job Title: System Administrator | Department: IT | Usage Location: FR",
      },
      {
        number: 4,
        title: 'Verifier les profils',
        detail: 'Confirmer que les deux comptes sont actifs et que les attributs jobTitle sont correctement remplis.',
      },
    ],
    criteria: [
      { id: 1, text: 'Compte nina.martin existe et est actif' },
      { id: 2, text: "Job Title = 'Cloud Administrator' pour Nina" },
      { id: 3, text: 'Compte luc.durand existe et est actif' },
      { id: 4, text: "Job Title = 'System Administrator' pour Luc" },
      { id: 5, text: 'Usage Location renseignee pour les deux comptes' },
    ],
    resources: [
      { title: 'Creer un utilisateur dans Entra ID', url: 'https://learn.microsoft.com/fr-fr/entra/fundamentals/how-to-create-delete-users', type: 'doc' },
      { title: 'Gestion des attributs utilisateur', url: 'https://learn.microsoft.com/fr-fr/entra/identity/users/groups-dynamic-membership', type: 'doc' },
    ],
  },
  {
    itemId: 6,
    title: 'Activer les licences Entra ID P2',
    subtitle: 'US-EN-LIC-002 — Attribution des licences P2',
    points: 15,
    priority: 'High',
    difficulty: 'Facile',
    estimatedTime: '10 min',
    objective:
      "Activer les licences Microsoft Entra ID P2 dans le tenant et les assigner aux comptes nina.martin et luc.durand pour debloquer les groupes dynamiques et le Conditional Access.",
    context:
      "Nina vient d'activer les superpouvoirs d'Entra ID. Les licences P2 sont la cle d'acces aux fonctionnalites avancees : groupes dynamiques, Conditional Access, Privileged Identity Management. Sans elles, l'automatisation est impossible. C'est un prerequis bloquant pour la suite de la mission.",
    steps: [
      {
        number: 1,
        title: 'Acceder aux licences',
        detail: 'portal.azure.com > Microsoft Entra ID > Licenses > All products',
      },
      {
        number: 2,
        title: 'Activer Entra ID P2',
        detail: 'Cliquer sur Try / Buy > Microsoft Entra ID P2 > Free trial > Activate',
      },
      {
        number: 3,
        title: 'Acceder aux utilisateurs licencies',
        detail: 'Licences > Microsoft Entra ID P2 > Licensed users > + Assign',
      },
      {
        number: 4,
        title: 'Assigner a nina.martin',
        detail: 'Dans le panneau Assign > Add users and groups > Rechercher nina.martin > Selectionner > Save',
      },
      {
        number: 5,
        title: 'Assigner a luc.durand',
        detail: 'Repeter la procedure > Rechercher luc.durand > Selectionner > Save',
      },
      {
        number: 6,
        title: 'Verifier les attributions',
        detail: "Profil de nina.martin > Licenses > Confirmer Microsoft Entra ID P2 = Active. Repeter pour luc.durand.",
      },
      {
        number: 7,
        title: 'Valider les fonctionnalites debloquees',
        detail: 'Entra ID > Groups > Verifier que le type Dynamic User est maintenant disponible a la creation.',
      },
    ],
    criteria: [
      { id: 1, text: 'Licence Entra ID P2 active dans le tenant' },
      { id: 2, text: 'Licence assignee a nina.martin' },
      { id: 3, text: 'Licence assignee a luc.durand' },
      { id: 4, text: 'Les fonctionnalites P2 sont disponibles dans le portail (Dynamic User visible)' },
    ],
    resources: [
      { title: 'Licences Entra ID', url: 'https://learn.microsoft.com/fr-fr/entra/fundamentals/licensing', type: 'doc' },
      { title: 'Assigner des licences dans Entra ID', url: 'https://learn.microsoft.com/fr-fr/entra/fundamentals/license-users-groups', type: 'video' },
    ],
  },
  {
    itemId: 9,
    title: 'Groupes dynamiques bases sur les roles',
    subtitle: 'US-EN-GRP-003 — Automatisation des acces par jobTitle',
    points: 20,
    priority: 'Medium',
    difficulty: 'Moyen',
    estimatedTime: '20 min',
    objective:
      "Creer trois groupes dans Entra ID : IT Cloud Administrators et IT System Administrators en mode dynamique (bases sur le jobTitle), et IT Lab Administrators en mode assigne.",
    context:
      "Nina a explique : 'Ces groupes, ce n'est pas juste pour filtrer. C'est notre moteur d'automatisation de l'acces.' Un groupe dynamique est un squelette structurel — il existe meme sans membre et le moteur evalue les regles en continu.",
    steps: [
      {
        number: 1,
        title: "Creer IT Cloud Administrators (dynamique)",
        detail: "Entra ID > Groups > New Group > Security | Membership type: Dynamic User | Nom: IT Cloud Administrators | Dynamic query: (user.jobTitle -eq 'Cloud Administrator')",
      },
      {
        number: 2,
        title: "Creer IT System Administrators (dynamique)",
        detail: "New Group > Security | Membership type: Dynamic User | Nom: IT System Administrators | Dynamic query: (user.jobTitle -eq 'System Administrator')",
      },
      {
        number: 3,
        title: "Creer IT Lab Administrators (assigne)",
        detail: "New Group > Security | Membership type: Assigned | Nom: IT Lab Administrators | Ajouter les membres manuellement",
      },
      {
        number: 4,
        title: "Valider les regles dynamiques",
        detail: "Sur chaque groupe dynamique : Validate Rules > tester avec les comptes nina.martin et luc.durand > verifier l'appartenance correcte",
      },
      {
        number: 5,
        title: "Verifier les membres",
        detail: "Attendre 5-10 min > Verifier dans Members que les utilisateurs sont automatiquement ajoutes selon leur jobTitle",
      },
    ],
    criteria: [
      { id: 1, text: "Groupe 'IT Cloud Administrators' existe avec adhesion dynamique" },
      { id: 2, text: "Nina est membre automatiquement (jobTitle = Cloud Administrator)" },
      { id: 3, text: "Groupe 'IT System Administrators' existe avec adhesion dynamique" },
      { id: 4, text: "Luc est membre automatiquement (jobTitle = System Administrator)" },
      { id: 5, text: "Groupe 'IT Lab Administrators' existe avec adhesion assignee" },
    ],
    resources: [
      { title: 'Groupes dynamiques Entra ID', url: 'https://learn.microsoft.com/fr-fr/entra/identity/users/groups-create-rule', type: 'doc' },
      { title: 'Syntaxe des regles de groupe dynamique', url: 'https://learn.microsoft.com/fr-fr/entra/identity/users/groups-dynamic-membership', type: 'doc' },
    ],
  },
  {
    itemId: 12,
    title: "Creer un tenant isole pour la R&D",
    subtitle: 'US-EN-TEN-004 — Isolation du tenant EgoCrisis Labs',
    points: 25,
    priority: 'Critical',
    difficulty: 'Difficile',
    estimatedTime: '25 min',
    objective:
      "Creer un tenant Azure AD secondaire (Cloudchrono Labs) pour isoler l'environnement R&D de la production, creer un utilisateur lab, et l'inviter comme guest dans le tenant principal.",
    context:
      "Le debat tenant vs resource group est tranché : la frontiere physique l'emporte. Un resource group isole peut etre contourne si un compte est compromis. Un tenant separe garantit une isolation totale : politiques independantes, logs separes, destruction sans risque pour la prod. Nina l'a confirme dans le Nexus.",
    steps: [
      {
        number: 1,
        title: "Creer le tenant Cloudchrono Labs",
        detail: "portal.azure.com > Manage tenants > Create > Microsoft Entra ID > Workforce tenant | Organization name: Cloudchrono Labs | Domain: cloudchronolabs.onmicrosoft.com",
      },
      {
        number: 2,
        title: "Basculer vers le nouveau tenant",
        detail: "Click sur le nom du tenant en haut a droite > Switch to Cloudchrono Labs",
      },
      {
        number: 3,
        title: "Creer labuser1",
        detail: "Dans Cloudchrono Labs > Entra ID > Users > New user > labuser1@cloudchronolabs.onmicrosoft.com | Display Name: Lab User 1",
      },
      {
        number: 4,
        title: "Creer labuser2",
        detail: "Dans Cloudchrono Labs > Entra ID > Users > New user > labuser2@cloudchronolabs.onmicrosoft.com | Display Name: Lab User 2",
      },
      {
        number: 5,
        title: "Configurer les roles dans le tenant labs",
        detail: "Entra ID > Roles and administrators > Assigner le role Reader a labuser1 et labuser2 dans le tenant Cloudchrono Labs",
      },
      {
        number: 6,
        title: "Revenir au tenant principal",
        detail: "Switch back au tenant principal > Entra ID > Users",
      },
      {
        number: 7,
        title: "Inviter labuser1 comme external guest",
        detail: "New user > Invite external user > Email: labuser1@cloudchronolabs.onmicrosoft.com | Send invitation",
      },
      {
        number: 8,
        title: "Inviter labuser2 comme external guest",
        detail: "New user > Invite external user > Email: labuser2@cloudchronolabs.onmicrosoft.com | Send invitation",
      },
      {
        number: 9,
        title: "Configurer les permissions guest",
        detail: "Entra ID > External Identities > External collaboration settings > Verifier que Guest user access = limited",
      },
      {
        number: 10,
        title: "Valider l'isolation",
        detail: "Connecter labuser1 au tenant principal > Verifier qu'il ne peut pas acceder aux ressources de la prod > Confirmer l'isolation effective",
      },
    ],
    criteria: [
      { id: 1, text: 'Tenant Cloudchrono Labs existe et est actif' },
      { id: 2, text: 'labuser1 et labuser2 sont crees dans le tenant Cloudchrono Labs' },
      { id: 3, text: 'labuser1 et labuser2 sont invites comme external guests dans le tenant principal' },
      { id: 4, text: "Les utilisateurs de la prod ne sont pas accessibles depuis le tenant labs" },
    ],
    resources: [
      { title: 'Creer un tenant Entra ID', url: 'https://learn.microsoft.com/fr-fr/entra/fundamentals/create-new-tenant', type: 'doc' },
      { title: 'B2B Collaboration — Invitations', url: 'https://learn.microsoft.com/fr-fr/entra/external-id/what-is-b2b', type: 'doc' },
    ],
  },
  {
    itemId: 15,
    title: 'Appliquer MFA aux comptes privilegies',
    subtitle: 'US-EN-MFA-005 — Conditional Access pour IT Cloud Administrators',
    points: 30,
    priority: 'Critical',
    difficulty: 'Difficile',
    estimatedTime: '20 min',
    objective:
      "Creer une politique Conditional Access qui force le MFA pour tous les membres du groupe IT Cloud Administrators lors de l'acces aux Microsoft Admin Portals.",
    context:
      "Un studio partenaire a ete compromis : compte admin sans MFA, token vole via fuite GitHub, 4 jours d'acces total a la prod. La lecon est claire — on considere tout compte admin comme compromis par defaut. Le MFA est non negociable. On construit des murs, pas des portes a cadenas.",
    steps: [
      {
        number: 1,
        title: 'Acceder au Conditional Access',
        detail: 'portal.azure.com > Microsoft Entra ID > Security > Conditional Access > New policy',
      },
      {
        number: 2,
        title: 'Nommer la politique',
        detail: "Nom: MFA for Cloud Administrators",
      },
      {
        number: 3,
        title: 'Definir les utilisateurs cibles',
        detail: 'Users > Include > Select users and groups > Groups > Selectionner IT Cloud Administrators',
      },
      {
        number: 4,
        title: 'Exclure le compte de secours',
        detail: 'Users > Exclude > Select users and groups > Ajouter le compte break-glass admin pour eviter le lockout',
      },
      {
        number: 5,
        title: 'Definir les ressources cibles',
        detail: 'Target resources > Cloud apps > Select apps > Microsoft Admin Portals',
      },
      {
        number: 6,
        title: "Configurer les conditions (optionnel)",
        detail: "Conditions > Locations > Exclude > Trusted locations si applicable dans votre contexte",
      },
      {
        number: 7,
        title: "Configurer le controle d'acces",
        detail: 'Grant > Grant access > Require multifactor authentication > Select',
      },
      {
        number: 8,
        title: 'Configurer la session (optionnel)',
        detail: 'Session > Sign-in frequency > Every 1 hour pour les acces privilegies',
      },
      {
        number: 9,
        title: 'Tester en mode Report-only',
        detail: 'Enable policy > Report-only > Create > Verifier les logs dans Sign-in logs pour valider le ciblage',
      },
      {
        number: 10,
        title: 'Activer la politique',
        detail: 'Modifier la politique > Enable policy > On > Save',
      },
      {
        number: 11,
        title: 'Valider le MFA en conditions reelles',
        detail: "Se connecter avec le compte nina.martin au portal.azure.com > Verifier que le challenge MFA est bien declenche",
      },
    ],
    criteria: [
      { id: 1, text: "Politique 'MFA for Cloud Administrators' existe et est activee" },
      { id: 2, text: "Users cibles : groupe IT Cloud Administrators" },
      { id: 3, text: "Target resource : Microsoft Admin Portals" },
      { id: 4, text: "Grant control : Require MFA" },
      { id: 5, text: "Enable policy : On" },
    ],
    resources: [
      { title: 'Conditional Access dans Entra ID', url: 'https://learn.microsoft.com/fr-fr/entra/identity/conditional-access/overview', type: 'doc' },
      { title: 'Deployer MFA avec Conditional Access', url: 'https://learn.microsoft.com/fr-fr/entra/identity/conditional-access/howto-conditional-access-policy-all-users-mfa', type: 'video' },
    ],
  },
];

export function getTaskData(itemId: number): TaskDetail | undefined {
  return TASK_DATA.find(t => t.itemId === itemId);
}
